import ApiService from '@/common/api.service'
import * as utils from '@/common/utils'
import * as findUnitsWidgetHelpers from '@/store/units/findUnitsWidget.helpers'

const module = 'admin/apiBridgeRabbitMqModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    name: '',
    partner: {
      id: null,
      name: null
    },
    agency: {
      id: null,
      name: null
    },
    clients: [],
    created_at: '',
    updated_at: '',
    enabled: true,
    virtual_host: '',
    exchange_name: '',
    queue_name: '',
    username: '',
    password: '',
    submit_gps: true,
    submit_trips: false,
    submit_events: false,
    queue_max_length: 0,
    max_submit_per_sec: 0,
    unit_submit_dur_sec: 0,
    binding_routing_key: '',
    publish_key_template: '',
    binding_per_client_id: false,
    skip_rabbitmq_setup: false,
    selection_mode: 'all',
    units: [],
    // Simply keeps a list of selected unit ids so that we can
    // determine if any changes were made to the API bridge by the user
    selected_unit_ids: []
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {}
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param apiBridge      Object
 *
 * @return Object
 */
function transformCreate (apiBridge) {
  let unit_ids = []
  if (apiBridge.selection_mode === 'selected') {
    unit_ids = findUnitsWidgetHelpers.getSelectedUnitIds('bridge_units')
  }

  return {
    id: apiBridge.id,
    name: apiBridge.name,
    enabled: apiBridge.enabled,
    client_ids: apiBridge.clients.map(client => client.id),
    virtual_host: apiBridge.virtual_host,
    exchange_name: apiBridge.exchange_name,
    queue_name: apiBridge.queue_name,
    username: apiBridge.username,
    password: apiBridge.password,
    submit_gps: apiBridge.submit_gps,
    submit_trips: apiBridge.submit_trips,
    submit_events: apiBridge.submit_events,
    queue_max_length: apiBridge.queue_max_length,
    max_submit_per_sec: apiBridge.max_submit_per_sec,
    unit_submit_dur_sec: apiBridge.unit_submit_dur_sec,
    binding_routing_key: apiBridge.binding_routing_key,
    publish_key_template: apiBridge.publish_key_template,
    binding_per_client_id: apiBridge.binding_per_client_id,
    skip_rabbitmq_setup: apiBridge.skip_rabbitmq_setup,
    selection_mode: apiBridge.selection_mode,
    unit_ids: unit_ids
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param apiBridge  Object
 *
 * @return Object
 */
function transformUpdate (apiBridge) {
  let unit_ids = []
  if (apiBridge.selection_mode === 'selected') {
    unit_ids = findUnitsWidgetHelpers.getSelectedUnitIds('bridge_units')
  }

  return {
    id: apiBridge.id,
    name: apiBridge.name,
    enabled: apiBridge.enabled,
    client_ids: apiBridge.clients.map(client => client.id),
    virtual_host: apiBridge.virtual_host,
    exchange_name: apiBridge.exchange_name,
    queue_name: apiBridge.queue_name,
    username: apiBridge.username,
    password: apiBridge.password,
    submit_gps: apiBridge.submit_gps,
    submit_trips: apiBridge.submit_trips,
    submit_events: apiBridge.submit_events,
    queue_max_length: apiBridge.queue_max_length,
    max_submit_per_sec: apiBridge.max_submit_per_sec,
    unit_submit_dur_sec: apiBridge.unit_submit_dur_sec,
    binding_routing_key: apiBridge.binding_routing_key,
    publish_key_template: apiBridge.publish_key_template,
    binding_per_client_id: apiBridge.binding_per_client_id,
    skip_rabbitmq_setup: apiBridge.skip_rabbitmq_setup,
    selection_mode: apiBridge.selection_mode,
    unit_ids: unit_ids
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: properties(),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }

}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`api-bridges/rabbitmq/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`api-bridges/rabbitmq/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`api-bridges/rabbitmq/${id}/edit/`)
      .then((response) => {
        const units = response.data.data.units
        findUnitsWidgetHelpers.addSelectedUnits('bridge_units', units)
        context.commit('set', response.data.data)
        context.commit('setProp', { name: 'selected_unit_ids', value: units.map(unit => unit.id) })
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    const apiBridge = context.getters['get']
    return ApiService
      .post(`api-bridges/rabbitmq`, transformCreate(apiBridge))
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const apiBridge = context.getters['get']
    return ApiService
      .put(`api-bridges/rabbitmq/${apiBridge.id}`, transformUpdate(apiBridge))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`api-bridges/rabbitmq/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`api-bridges/rabbitmq`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data.id = data.id
    state.data.name = data.name
    state.data.partner.id = data.partner.id
    state.data.partner.name = data.partner.name
    state.data.agency.id = data.agency.id
    state.data.agency.name = data.agency.name
    state.data.clients = data.clients
    state.data.virtual_host = data.virtual_host
    state.data.exchange_name = data.exchange_name
    state.data.queue_name = data.queue_name
    state.data.username = data.username
    state.data.password = ''
    state.data.submit_gps = data.submit_gps
    state.data.submit_trips = data.submit_trips
    state.data.submit_events = data.submit_events
    state.data.queue_max_length = data.queue_max_length
    state.data.max_submit_per_sec = data.max_submit_per_sec
    state.data.unit_submit_dur_sec = data.unit_submit_dur_sec
    state.data.binding_routing_key = data.binding_routing_key
    state.data.publish_key_template = data.publish_key_template
    state.data.binding_per_client_id = data.binding_per_client_id
    state.data.skip_rabbitmq_setup = data.skip_rabbitmq_setup
    state.data.selection_mode = data.selection_mode
    state.data.created_at = data.created_at
    state.data.updated_at = data.updated_at
  },

  setFormData (state, data) {
    state.form = {}
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original = {
      name: data.name,
      enabled: data.enabled,
      partner: data.partner,
      agency: data.agency,
      clients: data.clients,
      virtual_host: data.virtual_host,
      exchange_name: data.exchange_name,
      queue_name: data.queue_name,
      username: data.username,
      password: data.password,
      submit_gps: data.submit_gps,
      submit_trips: data.submit_trips,
      submit_events: data.submit_events,
      queue_max_length: data.queue_max_length,
      max_submit_per_sec: data.max_submit_per_sec,
      unit_submit_dur_sec: data.unit_submit_dur_sec,
      binding_routing_key: data.binding_routing_key,
      publish_key_template: data.publish_key_template,
      binding_per_client_id: data.binding_per_client_id,
      skip_rabbitmq_setup: data.skip_rabbitmq_setup,
      selection_mode: data.selection_mode,
      units: data.units
    }
  },

  /**
   * Add a client to the array of clients.
   *
   * @param state
   * @param client
   */
  addClient (state, client) {
    state.data.clients.push(client)
  },

  /**
   * Clear all clients from the user.
   *
   * @param state
   */
  clearClients (state) {
    state.data.clients = []
  },

  /**
   * Remove a client from the user based on the client's id.
   *
   * @param state
   * @param clientId
   */
  removeClientById (state, clientId) {
    const clientIdx = state.data.clients.findIndex(client => client.id === clientId)
    if (clientIdx > -1) {
      state.data.clients.splice(clientIdx, 1)
    }
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/administration/apiBridges/apiBridgeRabbitMqModule.js